/* iter195 — Follow-Ups queue page (/admin/followups).
   ------------------------------------------------------------
   Every job completion with a non-empty `recommended_followups` from the
   tech shows up here. Office moves each row through the workflow:
     new → contacted → scheduled → declined | closed
   The Field Completion Details panel opens on click for full context.
   ------------------------------------------------------------ */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  MessageSquare, Phone, Mail, MapPin, User, Clock, CheckCircle2, XCircle,
  Calendar, ArrowRight, Search,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import FieldCompletionDetails from '../../components/FieldCompletionDetails';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_TONES = {
  new:       'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40',
  contacted: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  scheduled: 'bg-green-500/15 text-green-300 border-green-500/40',
  declined:  'bg-[#262626] text-[#A3A3A3] border-[#404040]',
  closed:    'bg-[#262626] text-[#A3A3A3] border-[#404040]',
};

const fmtDT = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const StatusPill = ({ status }) => (
  <span
    data-testid={`followup-status-${status}`}
    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded ${STATUS_TONES[status] || STATUS_TONES.new}`}
  >
    {status}
  </span>
);

const AdminFollowups = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('new');
  const [q, setQ] = useState('');
  const [counts, setCounts] = useState({});
  const [drawer, setDrawer] = useState({ open: false, row: null });
  const [note, setNote] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter && filter !== 'all') params.set('status', filter);
      if (q.trim()) params.set('q', q.trim());
      const [{ data }, { data: c }] = await Promise.all([
        axios.get(`${API}/admin/followups?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/admin/followups/counts`, { withCredentials: true }),
      ]);
      setRows(Array.isArray(data) ? data : []);
      setCounts(c || {});
    } catch { setRows([]); }
    setLoading(false);
  }, [filter, q]);

  useEffect(() => { refresh(); }, [refresh]);

  const openDrawer = (row) => { setDrawer({ open: true, row }); setNote(''); };
  const closeDrawer = () => { setDrawer({ open: false, row: null }); setNote(''); };

  const updateRow = async (row, patch, msg) => {
    try {
      const { data } = await axios.put(`${API}/admin/followups/${row.completion_id}`, patch, { withCredentials: true });
      toast.success(msg || 'Follow-up updated');
      // Local optimistic patch so the drawer reflects immediately.
      const merged = { ...row, ...data };
      setRows((rs) => rs.map((r) => (r.completion_id === row.completion_id ? merged : r)));
      if (drawer.open && drawer.row?.completion_id === row.completion_id) {
        setDrawer({ open: true, row: merged });
      }
      // Counts change → refresh those separately.
      axios.get(`${API}/admin/followups/counts`, { withCredentials: true })
        .then((r) => setCounts(r.data || {}))
        .catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const setStatus = (row, status) => updateRow(row, { status }, `Marked ${status}`);
  const setPriority = (row, priority) => updateRow(row, { priority }, `Priority: ${priority}`);
  const addNote = async () => {
    if (!note.trim() || !drawer.row) return;
    await updateRow(drawer.row, { note: note.trim() }, 'Note added');
    setNote('');
  };

  return (
    <div data-testid="admin-followups-page" className="space-y-6">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-[#D4AF37]" /> Follow-Ups
        </h2>
        <p className="text-[#C9C9C9] text-sm">Recommended follow-ups the field techs flagged. Every one has a customer waiting for an office call.</p>
      </div>

      {/* Filter strip */}
      <div className="flex items-center gap-2 flex-wrap">
        {['new', 'contacted', 'scheduled', 'declined', 'closed', 'all'].map((s) => (
          <Button
            key={s}
            data-testid={`followup-filter-${s}`}
            size="sm"
            onClick={() => setFilter(s)}
            variant={filter === s ? 'default' : 'outline'}
            className={filter === s
              ? 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold'
              : 'border-[#262626] text-white hover:bg-[#262626]'}
          >
            {s.toUpperCase()}
            {counts[s] !== undefined && s !== 'all' && (
              <span className="ml-1.5 text-[10px] bg-black/40 rounded px-1.5 py-0.5">{counts[s]}</span>
            )}
          </Button>
        ))}
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none" />
          <Input
            data-testid="followup-search"
            type="text"
            placeholder="Search customer or tech…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-[#0A0A0A] border-[#262626] text-white pl-8 h-9"
          />
        </div>
      </div>

      {loading && <p className="text-[#A3A3A3] text-sm">Loading…</p>}
      {!loading && rows.length === 0 && (
        <div data-testid="followup-empty" className="bg-[#141414] border border-[#262626] rounded p-6 text-center text-[#A3A3A3] text-sm">
          No follow-ups in this view.
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <div
            key={r.completion_id}
            data-testid={`followup-row-${r.completion_id}`}
            className="bg-[#141414] border border-[#262626] rounded p-3 hover:bg-[#1a1a1a] cursor-pointer"
            onClick={() => openDrawer(r)}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <User className="w-4 h-4 text-[#D4AF37]" />
                  <p className="text-white font-bold text-sm">{r.customer_name || 'Customer'}</p>
                  <StatusPill status={r.followup_status || 'new'} />
                  {r.followup_priority && r.followup_priority !== 'normal' && (
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border rounded ${
                      r.followup_priority === 'high' ? 'bg-red-500/15 text-red-300 border-red-500/40' : 'bg-[#262626] text-[#C9C9C9] border-[#404040]'
                    }`}>
                      {r.followup_priority}
                    </span>
                  )}
                  {r.invoice_status && (
                    <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] bg-[#262626] px-1.5 py-0.5 rounded">
                      {String(r.invoice_status).replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
                <p className="text-white text-sm mt-2 whitespace-pre-wrap line-clamp-3" data-testid={`followup-text-${r.completion_id}`}>
                  {r.recommended_followups}
                </p>
                <div className="mt-2 flex items-center gap-3 text-xs text-[#A3A3A3] flex-wrap">
                  <span className="flex items-center gap-1"><User className="w-3 h-3" /> {r.employee_name || 'tech'}</span>
                  {r.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {r.date}</span>}
                  {r.customer_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {r.customer_phone}</span>}
                  {r.customer_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {r.customer_email}</span>}
                  {r.submitted_at && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDT(r.submitted_at)}</span>}
                </div>
              </div>
              {(r.followup_status || 'new') === 'new' && (
                <div className="flex flex-col gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Button
                    data-testid={`followup-mark-contacted-${r.completion_id}`}
                    size="sm"
                    onClick={() => setStatus(r, 'contacted')}
                    className="bg-blue-600 hover:bg-blue-700 text-white h-8"
                  >
                    <Phone className="w-3.5 h-3.5 mr-1" /> Mark Contacted
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Detail drawer */}
      <Sheet open={drawer.open} onOpenChange={(o) => { if (!o) closeDrawer(); }}>
        <SheetContent side="right" data-testid="followup-drawer" className="bg-[#0A0A0A] border-[#262626] text-white w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-[#D4AF37] flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> Follow-Up · {drawer.row?.customer_name || ''}
            </SheetTitle>
          </SheetHeader>

          {drawer.row && (
            <div className="mt-4 space-y-4">
              {/* Status + priority controls */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-white text-xs">Status</Label>
                  <Select
                    value={drawer.row.followup_status || 'new'}
                    onValueChange={(v) => setStatus(drawer.row, v)}
                  >
                    <SelectTrigger data-testid="followup-status-select" className="bg-[#141414] border-[#262626] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626] text-white">
                      {['new', 'contacted', 'scheduled', 'declined', 'closed'].map((s) => (
                        <SelectItem key={s} value={s} data-testid={`followup-status-opt-${s}`}>{s.toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white text-xs">Priority</Label>
                  <Select
                    value={drawer.row.followup_priority || 'normal'}
                    onValueChange={(v) => setPriority(drawer.row, v)}
                  >
                    <SelectTrigger data-testid="followup-priority-select" className="bg-[#141414] border-[#262626] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626] text-white">
                      {['low', 'normal', 'high'].map((p) => (
                        <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Add office note */}
              <div>
                <Label className="text-white text-xs">Add Office Note</Label>
                <Textarea
                  data-testid="followup-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Called Adam — leaving VM. Will retry Fri…"
                  rows={2}
                  className="bg-[#141414] border-[#262626] text-white"
                />
                <div className="mt-2 flex gap-2">
                  <Button
                    data-testid="followup-add-note"
                    size="sm"
                    onClick={addNote}
                    disabled={!note.trim()}
                    className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
                  >
                    Add Note
                  </Button>
                  {drawer.row.customer_id && (
                    <Link to={`/admin/customers/${drawer.row.customer_id}`}>
                      <Button variant="outline" size="sm" className="border-[#262626] text-white hover:bg-[#262626]">
                        Open Customer <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>

              {/* Full Field Completion Details */}
              <FieldCompletionDetails completionId={drawer.row.completion_id} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdminFollowups;
